/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Created by jessenelson on 8/28/15.
 */

'use strict';

function PackageDistributionViewController(
    _,
    $q,
    $routeParams,
    $scope,
    $timeout,
    ContentResourceFactory,
    MetadataService,
    ModalService,
    NotificationService,
    PackageLiveClipViewService,
    PackageResourceFactory,
    PackageUtilService,
    PartnerEditService,
    PartnerPackagesService,
    PermissionsService,
    SubscriptionsResourceFactory,
    UtilsService
) {

    let self = this;

    let queryId;

    //=============//
    // Permissions //
    //=============//

    let DISTRIBUTION_TAB_VIEW_STATE = 'PACKAGE_DISTRIBUTIONGRID_VIEW_STATE';
    let ADVANCED_DISTRIBUTION_TAB_VIEW_STATE = 'ADVANCED_PACKAGE_DISTRIBUTIONGRID_VIEW_STATE';
    let CLIP_DISTRIBUTION_GRID_VIEW_STATE = 'CLIP_DISTRIBUTIONGRID_VIEW_STATE';
    let ADVANCED_LIVE_CLIP_PACKAGE_DISTRIBUTIONGRID_VIEW_STATE = 'ADVANCED_LIVE_CLIP_PACKAGE_DISTRIBUTIONGRID_VIEW_STATE';
    let PACKAGE_BRANDING_VIEW_STATE = 'PACKAGE_BRANDING_VIEW_STATE';
    let CLIP_ACTION_BRANDING_VIEW_STATE = 'CLIP_ACTION_BRANDING_VIEW_STATE';
    let BASE_TEN_RADIX = 10;


    let getPermissions = function () {
        self.basicViewState = (
            self.isClipDistribution ?
                CLIP_DISTRIBUTION_GRID_VIEW_STATE :
                DISTRIBUTION_TAB_VIEW_STATE);

        self.advancedViewState = (
            self.isClipDistribution ?
                ADVANCED_LIVE_CLIP_PACKAGE_DISTRIBUTIONGRID_VIEW_STATE :
                ADVANCED_DISTRIBUTION_TAB_VIEW_STATE
        );

        self.viewState = self.isAdvanced ? self.advancedViewState : self.basicViewState;

        let writePromise = PermissionsService.canWrite(self.basicViewState);
        let advancedWritePromise = PermissionsService.canWrite(self.advancedViewState);
        let brandingOverridesPromise = PermissionsService.getPermissionsForViewState(PACKAGE_BRANDING_VIEW_STATE);
        let clipBrandingOverridesPromise = PermissionsService.getPermissionsForViewState(CLIP_ACTION_BRANDING_VIEW_STATE);

        return $q.all({
            basic: writePromise,
            advanced: advancedWritePromise,
            branding: brandingOverridesPromise,
            clipBranding: clipBrandingOverridesPromise
        }).then(function (perms) {
            self.writePerms = self.isAdvanced ? perms.advanced : perms.basic;
            self.canViewBrandingOverrides = self.isClipDistribution ?
                PermissionsService.hasReadPermission(perms.clipBranding.permission) :
                PermissionsService.hasReadPermission(perms.branding.permission);
        });
    };


    //================//
    // Dynamic Styles //
    //================//

    // Used to impose different stylesheet styles for the main
    // package dist. grids and the clip dist. grids.

    let liveMetadataOverrides = {
        episodeNumber: {
            columnClass: 'col-sm-4 pad-sm-right'
        },
        textinput: {
            labelClass: 'prism-form-item-label col-sm-' + (self.isAdvanced ? '3' : '12'),
            requiredClass: 'required-text-input-lg'
        },
        datetimefield: {
            labelClass: 'prism-form-item-label col-sm-' + (self.isAdvanced ? '3' : '12'),
            requiredClass: 'required-text-input-lg'
        },
        datetimefieldenhanced: {
            labelClass: 'prism-form-item-label col-sm-' + (self.isAdvanced ? '3' : '12'),
            requiredClass: 'required-text-input-lg'
        },
        datefieldenhanced: {
            labelClass: 'prism-form-item-label col-sm-' + (self.isAdvanced ? '3' : '12'),
            requiredClass: 'required-text-input-lg'
        },
        datefield: {
            labelClass: 'prism-form-item-label col-sm-' + (self.isAdvanced ? '3' : '12'),
            requiredClass: 'required-text-input-lg'
        },
        textarea: {
            labelClass: 'prism-form-item-label col-sm-' + (self.isAdvanced ? '3' : '12'),
            requiredClass: 'required-text-area-3-lg'
        },
        timeinput: {
            labelClass: 'prism-form-item-label col-sm-' + (self.isAdvanced ? '3' : '12'),
            columnClass: 'col-sm-4 time-component top-margin pad-sm-right',
            requiredClass: 'required-time-picker-lg'
        },
        timecodeinput: {
            labelClass: 'prism-form-item-label col-sm-' + (self.isAdvanced ? '3' : '12'),
            requiredClass: 'required-text-input-lg',
            attributes: {
                'root-class': 'col-sm-4'
            }
        },
        checkbox: {
            labelClass: 'prism-form-item-label col-sm-' + (self.isAdvanced ? '3' : '12'),
            requiredClass: 'required-text-input-lg'
        },
        dropdownlist: {
            labelClass: 'prism-form-item-label col-sm-' + (self.isAdvanced ? '3' : '12'),
            warningClasses: 'metadata-icons-col'
        },
        checkboxlist: {
            labelClass: 'prism-form-item-label col-sm-' + (self.isAdvanced ? '3' : '12'),
            requiredClass: 'required-checkbox-list-lg'
        },
        list: {
            labelClass: 'prism-form-item-label col-sm-' + (self.isAdvanced ? '3' : '12'),
            requiredClass: 'required-list-lg'
        }
    };

    let mainPackageMetadataOverrides = {
        episodeNumber: {
            columnClass: 'col-sm-2 pad-sm-right'
        }
    };

    self.customMetadataOverrides = self.isClipDistribution ? liveMetadataOverrides : mainPackageMetadataOverrides;


    //============//
    // Select All //
    //============//

    self.selectAllPartnerPackages = false;

    self.resetSelectAll = function (enabled) {
        if (!enabled && self.selectAllPartnerPackages) {
            self.selectAllPartnerPackages = false;
        }
    };

    self.updatePartnerPackageSubscriptionSelection = function (select) {
        PackageUtilService.editorForm.$setDirty();
        _.forEach(self.partnerPackages, function (partnerPackage) {
            if (partnerPackage.enabled !== select) {
                partnerPackage.enabled = select;
            }
        });
    };

    let partnerSubscriptionAllSelected = function () {
        let allSelected = true;
        _.forEach(self.partnerPackages, function (partnerPackage) {
            if (allSelected && !partnerPackage.enabled) {
                allSelected = false;
            }
        });
        return allSelected;
    };


    //===============//
    // Branding Data //
    //===============//

    self.canViewBrandingOverrides = false;

    self.showBrandingOverridesModal = function (partnerPkg) {
        let brandingViewState = self.isClipDistribution ?
            CLIP_ACTION_BRANDING_VIEW_STATE :
            PACKAGE_BRANDING_VIEW_STATE;
        let readOnly = self.packageObjectData.locked || !PackageUtilService.statusAllowsEdit(self.packageObjectData.status);
        ModalService.showBrandingDistributionOverridesModal(
            _.cloneDeep(partnerPkg),
            self.packageObjectData,
            brandingViewState,
            readOnly,
            function (newPartnerPackage) {
                if (self.isClipDistribution) {
                    PackageLiveClipViewService.distributionForm.$setDirty();
                }
                PackageUtilService.editorForm.$setDirty();
                let brandingProps = [
                    'preRollOverride', 'postRollOverride', 'breakPrePostRollOverride', 'watermarkOverride',
                    'preRollOverridden', 'postRollOverridden', 'breakPrePostRollOverridden', 'watermarkOverridden',
                    'preRollSeasonVolume', 'postRollSeasonVolume', 'breakPrePostRollSeasonVolume', 'watermarkSeasonVolume'];
                _.forEach(brandingProps, function (prop) {
                    if (newPartnerPackage[prop] !== undefined) {
                        partnerPkg[prop] = newPartnerPackage[prop];
                    }
                });
            });
    };


    //===========//
    // Edit Data //
    //===========//

    self.partnerPackages = [];
    self.seasonPartnerPackages = [];
    self.volumePartnerPackages = [];

    self.partnerComponentsMap = {};

    self.videoRoleMap = {};

    let handleQuery = function () {
        _.forEach(PartnerPackagesService.partnerPackages(), function (partnerPackage) {
            self.videoRoleMap[partnerPackage.id] = _.cloneDeep(partnerPackage.videoRoles);
        });

        self.partnerPackages = PartnerPackagesService.partnerPackages();
        self.seasonPartnerPackages = PartnerPackagesService.seasonPartnerPackages();
        self.volumePartnerPackages = PartnerPackagesService.volumePartnerPackages();

        self.partnerComponentsMap = PartnerPackagesService.partnerComponentsMap();
    };

    let getEditData = function () {
        if (!self.packageObjectData.category) {
            return $q.when(true);
        }

        let promises = {};

        // get partner packages
        if (self.isClipDistribution) {
            let deferred = $q.defer();
            deferred.resolve(PartnerPackagesService.partnerPackages());
            promises.pkgs = deferred.promise;
        } else {
            promises.pkgs = PartnerPackagesService.queryPartnerPackages(queryId, self.packageObjectData);
        }

        // get content categories
        if (self.isAdvanced) {
            promises.contentCategories = PartnerPackagesService.queryContentCategories(self.packageObjectData);
        }

        // get seasons & volumes
        if (self.packageObjectData.content.type === 'Series') {
            promises.seasons = PartnerPackagesService.querySeasons($routeParams.id);
            promises.volumes = PartnerPackagesService.queryVolumes($routeParams.id);
        }

        return $q.all(promises).then(function (editData) {
            self.availableSeasons = editData.seasons;
            self.availableVolumes = editData.volumes;
            if (self.isAdvanced) {
                self.availableComponentRoles = [];
                let contentCategory = editData.contentCategories.filter(function (contentCategory) {
                    return contentCategory.category.id === self.packageObjectData.category.id;
                })[0];
                if (!!contentCategory) {
                    _.forEach(contentCategory.videoRoles, function (videoRole) {
                        if (videoRole.role.name !== 'Main Video') {
                            self.availableComponentRoles.push(videoRole.role);
                        }
                    });
                } else {
                    // Assume a default content category,
                    // as no explicit/customized one is
                    // being returned.
                    self.availableComponentRoles = [];
                }
            }

            handleQuery();
            self.selectAllPartnerPackages = partnerSubscriptionAllSelected();
        });
    };

    let createNewPartnerSubscription = function (partner, subType) {
        let partnerSub = {};
        partnerSub.enabled = true;
        partnerSub.category = self.packageObjectData.category;
        partnerSub.content = self.packageObjectData.content;
        partnerSub.partner = partner;
        partnerSub.adHocPackageId = self.packageObjectData.id;
        if (partnerSub.content.type === 'Series') {
            partnerSub.seasonSubscription = subType === 'season';
            partnerSub.seasons = [{season: self.packageObjectData.season}];
            partnerSub.volumes = subType !== 'season' ? [{volume: self.packageObjectData.volume}] : [];
        }
        return partnerSub;
    };

    let selectPartnerPackageInstance = function (partnerPackage, instanceId) {
        return PartnerPackagesService.selectPartnerPackageInstance(partnerPackage, instanceId).then(function () {
            // the service takes care of replacing instances
            // of partner packages in the "main" arrays, so we
            // just need to grab the new arrays
            self.partnerPackages = PartnerPackagesService.partnerPackages();
            self.seasonPartnerPackages = PartnerPackagesService.seasonPartnerPackages();
            self.volumePartnerPackages = PartnerPackagesService.volumePartnerPackages();

            self.partnerComponentsMap = PartnerPackagesService.partnerComponentsMap();
        });
    };

    self.canOverridePartnerBranding = function (partnerPkg) {
        return partnerPkg.enabled && self.canViewBrandingOverrides;
    };

    self.partnerHasBrandingOverrides = function (partnerPkg) {
        return (
            partnerPkg.preRollOverridden ||
            partnerPkg.postRollOverridden ||
            partnerPkg.breakPrePostRollOverridden ||
            partnerPkg.watermarkOverridden
        );
    };

    let getDataToSave = function () {
        let partnerPackagesSavePayload = {};

        _.forEach(PartnerPackagesService.partnerPackages(), function (partnerPackageToSave, index) {

            // don't save unmodified partner packages
            if (!PartnerPackagesService.isPartnerPackageModified(partnerPackageToSave)) {
                return;
            }

            partnerPackagesSavePayload[index] = _.cloneDeep(partnerPackageToSave);

            // **************************************************************** //
            // **************************************************************** //
            // **** Seriously! Always perform this action before you save  **** //
            // **** metadata items to ensure the correct data is persisted **** //
            // **************************************************************** //
            // **************************************************************** //
            // save metadata components
            _.forEach(PartnerPackagesService.partnerComponentsMap()[partnerPackageToSave.id][self.isAdvanced ? 'advanced' : 'basic'], (comp, name) => {
                if (comp.uiControlHint === 'TextListInput') {
                    partnerPackagesSavePayload[index].metadata.properties[name].value = UtilsService.arrayToString(comp.instanceData.value, MetadataService.textListInputProps.delimiter, MetadataService.textListInputProps.objectKey);
                } else {
                    partnerPackagesSavePayload[index].metadata.properties[name].value = comp.instanceData.value;
                }
            });
        });

        return _.values(partnerPackagesSavePayload);
    };

    let getBlankInstanceRevisionPartners = function () {
        let partners = [];

        _.forEach(PartnerPackagesService.partnerPackages(), function (partnerPackage) {
            let selectedInstance = PartnerPackagesService.getSelectedInstance(partnerPackage);
            if (selectedInstance && !selectedInstance.revision) {
                partners.push(partnerPackage.partner.name);
            }
        });

        return partners;
    };

    let onSave = function () {
        $scope.$emit('server-busy', {working: true});

        let partnersWithBlankInstanceRevisions = getBlankInstanceRevisionPartners();
        if (partnersWithBlankInstanceRevisions.length > 0) {
            let msg = 'The following partners have empty instance revision fields: ';
            msg += partnersWithBlankInstanceRevisions.join(', ');
            msg += '\n\nPlease populate these fields before saving.';
            NotificationService.createWarningNotification(msg);
            $scope.$emit('server-busy', {working: false});
            return;
        }

        let allPartrnerPackagesToSave = getDataToSave();

        let partnerPackagesToBatchSave = [];
        let partnerPackagesToSaveIndividually = [];

        let batchSavePromise;
        let savePromises = [];

        _.forEach(allPartrnerPackagesToSave, function (partPkg) {
            let individualSave = false;

            if (partPkg.partner.partnerDeliveryInstanceEnabled) {
                let mostRecentInstance = PartnerPackagesService.mostRecentDeliveryInstance(partPkg.id);
                let selectedInstance = PartnerPackagesService.selectedInstanceMap()[partPkg.id];

                if (mostRecentInstance !== selectedInstance) {
                    partnerPackagesToSaveIndividually.push(partPkg);
                    individualSave = true;
                }
            }

            if (!individualSave) {
                partnerPackagesToBatchSave.push(partPkg);
            }
        });

        batchSavePromise = $q.when(true);
        if (partnerPackagesToBatchSave.length > 0) {
            batchSavePromise = PackageResourceFactory.savePartnerPackages({
                id: queryId,
                sub: 'partnerPackages'
            }, partnerPackagesToBatchSave).$promise;
        }

        if (partnerPackagesToSaveIndividually.length > 0) {
            _.forEach(partnerPackagesToSaveIndividually, function (partPkg) {
                savePromises.push(PartnerPackagesService.updatePartnerPackage(partPkg));
            });
        }

        // save most recent instances first
        return batchSavePromise
            .then(function () {
                // save "old" instances individually
                return $q.all(savePromises);
            })
            .then(function () {
                // save instances
                return PartnerPackagesService.saveInstanceRevisions();
            })
            .then(function () {
                // refresh edit data
                return getEditData();
            })
            .then(function () {
                // re-select old instances
                _.forEach(partnerPackagesToSaveIndividually, function (partPkg) {
                    selectPartnerPackageInstance(partPkg, PartnerPackagesService.selectedInstanceMap()[partPkg.id]);
                });
            });
    };

    let onRevert = function () {
        $scope.$emit('server-busy', {working: true});
        PartnerPackagesService.revertPartnerPackages();

        _.forEach(PartnerPackagesService.partnerPackages(), function (partnerPackage) {
            self.videoRoleMap[partnerPackage.id] = _.cloneDeep(partnerPackage.videoRoles);
        });

        self.partnerPackages = PartnerPackagesService.partnerPackages();
        self.seasonPartnerPackages = PartnerPackagesService.seasonPartnerPackages();
        self.volumePartnerPackages = PartnerPackagesService.volumePartnerPackages();

        self.partnerComponentsMap = PartnerPackagesService.partnerComponentsMap();

        self.selectAllPartnerPackages = partnerSubscriptionAllSelected();

        $scope.$emit('server-busy', {working: false});
    };

    self.enabledVideoRolesUpdated = function (context, newEnabledEntities) {
        let partnerPackage = context;
        partnerPackage.videoRoles = newEnabledEntities;
        self.packageDistributionGridEditorForm.$setDirty();
    };

    self.onInstanceSelected = function (partnerPackage, $selected, $oldValue) {
        if (PartnerPackagesService.isPartnerPackageModified(partnerPackage)) {
            PartnerPackagesService.selectedInstanceMap()[partnerPackage.id] = $oldValue;
            NotificationService.createWarningNotification(
                'The current instance has been modified. Please save or revert your changes first.'
            );
            return;
        }

        $scope.$emit('server-busy', {working: true});

        let isDirty = PartnerPackagesService.isPartnerDataModified();

        let onSelectedPromise;
        if ($selected.createNew) {
            onSelectedPromise = PartnerPackagesService.createPartnerPackageInstance(partnerPackage);
            onSelectedPromise.then(function (instance) {
                return selectPartnerPackageInstance(partnerPackage, instance.id);
            });
        } else {
            onSelectedPromise = selectPartnerPackageInstance(partnerPackage, $selected.id);
        }
        onSelectedPromise.then(function () {
            if (!isDirty) {
                self.packageDistributionGridEditorForm.$setPristine();
            }

            $scope.$emit('server-busy', {working: false});
        });
    };


    //=================//
    // Ad-Hoc Packages //
    //=================//

    let addAdHocHandler = function (newSubs) {
        $scope.$emit('server-busy', {working: true});
        let partnerSubsPromises = [];
        let selectedPartners = newSubs.selectedPartners;
        _.forEach(selectedPartners, function (partner) {
            let newSub = createNewPartnerSubscription(partner, newSubs.subType);
            let subPromise = SubscriptionsResourceFactory.createSubscription(
                {partnerId: partner.id}, newSub).$promise;
            partnerSubsPromises.push(subPromise);
        });

        $q.all(partnerSubsPromises).then(function () {
            PartnerPackagesService.queryPartnerPackages(queryId, self.packageObjectData).then(function () {
                handleQuery(PartnerPackagesService.partnerPackages());
                $scope.$emit('server-busy', {working: false});
            });
        });
    };

    self.removeAdHocSub = function (partnerPackage) {
        $scope.$emit('server-busy', {working: true});
        partnerPackage.subscription.enabled = false;

        SubscriptionsResourceFactory.saveSubscription({
            partnerId: partnerPackage.partner.id,
            subId: partnerPackage.subscription.id
        }, partnerPackage.subscription).$promise
            .then(function () {
                return PackageResourceFactory.query({
                    id: queryId,
                    sub: 'partnerPackages'
                }).$promise;
            })
            .then(function (pkgs) {
                handleQuery(pkgs);
                $scope.$emit('server-busy', {working: false});
            });
    };

    PackageUtilService.tabAdHocPartnerFunction = function () {
        let selectedPartners = [];
        _.forEach(PartnerPackagesService.partnerPackages(), function (partnerPackage) {
            selectedPartners.push(partnerPackage.partner);
        });
        ModalService.showAdHocSelectPartnersModal(selectedPartners, self.packageObjectData.content.type === 'Series',
            !!self.packageObjectData.season, !!self.packageObjectData.volume, addAdHocHandler);
        self.selectAllPartnerPackages = partnerSubscriptionAllSelected();
    };


    //================//
    // Template Logic //
    //================//

    self.deliveryInstanceMap = PartnerPackagesService.deliveryInstanceMap;
    self.selectedInstanceMap = PartnerPackagesService.selectedInstanceMap;
    self.selectedInstanceRevisionAccessors = PartnerPackagesService.selectedInstanceRevisionAccessors;

    self.getSubscriptionTypeForPartnerPackage = function (partnerPackage) {
        if (partnerPackage.subscription.adHocPackageId) {
            return 'adHoc';

        } else if (partnerPackage.partner.numberOfChildren && parseInt(partnerPackage.partner.numberOfChildren, BASE_TEN_RADIX)) {
            return 'isSuperPartner';
        } else if (self.packageObjectData.content && self.packageObjectData.content.type === 'Series') {
            if (partnerPackage.subscription.seasonSubscription) {
                return (partnerPackage.subscription.seasons && partnerPackage.subscription.seasons.length) ?
                    'partialSeasons' :
                    'allSeasons';
            } else {
                return (partnerPackage.subscription.volumes && partnerPackage.subscription.volumes.length) ?
                    'partialVolumes' :
                    'allVolumes';
            }
        } else {
            return 'movieSpecial';
        }
    };

    self.statusAllowsEdit = function () {
        return PackageUtilService.statusAllowsEdit(self.packageObjectData.status) && !self.packageObjectData.isDelivering;
    };

    self.disableOverrides = function (partnerPackage) {
        return parseInt(partnerPackage.revision) > 1;
    };

    self.getInstanceLabel = function (partnerPackage) {
        if (!partnerPackage.partner.partnerDeliveryInstanceEnabled) {
            return '';
        }

        return PartnerPackagesService.deliveryInstanceMap()[partnerPackage.id].filter(function (instance) {
            return instance.id === PartnerPackagesService.selectedInstanceMap()[partnerPackage.id];
        })[0].label;
    };

    self.numericFieldKeyDown = function ($event, currentValue) {
        if ($event.key.length === 1) {
            let newValue = currentValue || '';
            newValue += $event.key;
            if (!(/^[0-9]+$/.test(newValue))) {
                $event.preventDefault();
            }
        }
    };

    self.showMMCInventoryModal = (partnerPackage) => {
        let readOnly = self.packageObjectData.locked || !self.statusAllowsEdit(self.packageObjectData.status) || !self.writePerms;
        ModalService.showMMCInventoryModal(partnerPackage.packageId, partnerPackage.partner.id, readOnly);
    };


    //=================//
    // Lifecycle Hooks //
    //=================//

    self.$postLink = function () {
        $timeout(function () {
            if (self.isClipDistribution) {
                PackageUtilService.editorForm = PackageLiveClipViewService[self.editorFormName];
            } else {
                PackageUtilService.editorForm = self.packageDistributionGridEditorForm;
            }
        });
    };

    self.$onDestroy = function () {
        if (self.isClipDistribution && self.editorFormName) {
            delete PackageUtilService.subTabSaveFunctions[self.editorFormName];
            delete PackageUtilService.subTabRevertFunctions[self.editorFormName];
        }

        self.partnerPackages = [];
        self.seasonPartnerPackages = [];
        self.volumePartnerPackages = [];

        self.partnerComponentsMap = {};
    };

    // **************************************************************** //
    // **************************************************************** //
    // **** Seriously! Always perform this action before you save  **** //
    // **** metadata items to ensure the correct data is persisted **** //
    // **************************************************************** //
    // **************************************************************** //
    self.onUpdate = (event) => {
        self.validationErrors = event.errors;
        if (!self.validationErrors) {
            _.forEach(PartnerPackagesService.partnerPackages(), (partnerPackageToSave, index) => {
                // update metadata components
                let textListInputComponent = PartnerPackagesService.partnerComponentsMap()[partnerPackageToSave.id][self.isAdvanced ? 'advanced' : 'basic'][event.name];
                if (textListInputComponent) {
                    textListInputComponent.instanceData.value = event.items;
                }
            });
        }
    };

    self.$onInit = function () {
        $scope.$emit('server-busy', {working: true});

        self.isClipDistribution = $routeParams.editView === 'liveClip';
        queryId = self.isClipDistribution ? self.packageObjectData.id : $routeParams.id;

        if (self.isClipDistribution && self.editorFormName) {
            PackageUtilService.subTabSaveFunctions[self.editorFormName] = {
                data: getDataToSave,
                callback: getEditData,
            };
            PackageUtilService.subTabRevertFunctions[self.editorFormName] = onRevert;
        } else {
            PackageUtilService.tabSaveFunction = onSave;
            PackageUtilService.tabRevertFunction = onRevert;
        }

        $q.all([
            getPermissions(),
            getEditData(),
        ])
            .then(function () {
                $scope.$emit('server-busy', {working: false});
            });
    };
}

angular
    .module('prism-ui.package')
    .component('packageDistributionGridView', {
        templateUrl: 'package/editor/components/view/distributionGrid/packagedistributiongrid-view-template.html',
        controller: PackageDistributionViewController,
        bindings: {
            packageObjectData: '=',
            isAdvanced: '=',
            editorFormName: '=',
        },
        transclude: true,
        require: {
            pkgLiveClip: '?^packageLiveclipView',
        }
    }).filter('stringToNumber', () => {
    return (numberAsString) => {
        return parseInt(numberAsString, 10);
    };
});
